import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
    { label: "Recruitment", href: "/recruitment" },
    { label: "Global Mobility", href: "/#global-mobility" },
    { label: "Study Abroad", href: "/#study-abroad" },
    { label: "Scholarships", href: "/#scholarships" },
    { label: "About", href: "/#about" },
    { label: "Contact", href: "/#contact" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        const desktopMenu = window.matchMedia("(min-width: 1536px)");
        const closeOnDesktop = (event) => {
            if (event.matches) setOpen(false);
        };
        desktopMenu.addEventListener("change", closeOnDesktop);
        return () => desktopMenu.removeEventListener("change", closeOnDesktop);
    }, []);

    const dark = !scrolled && !open;
    const lightMenu = open;

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
                    scrolled && !open
                        ? "border-b border-ink/10 bg-ivory/85 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-24 max-w-[1600px] items-center justify-between gap-4 px-4 sm:px-6 xl:px-8 2xl:px-10">
                    <a
                        href="/"
                        data-testid="logo-link"
                        className={`flex min-w-0 items-center gap-3 ${dark || lightMenu ? "text-ivory" : "text-ink"}`}
                    >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-forest font-serif text-sm font-semibold tracking-[0.12em] text-ivory">
                            AGL
                        </span>
                        <span className="min-w-0 leading-tight">
                            <span className="block truncate whitespace-nowrap font-serif text-sm font-semibold uppercase tracking-[0.12em] min-[400px]:text-base sm:text-lg sm:tracking-[0.14em] 2xl:text-xl">
                                Afrinexa Global
                            </span>
                            <span
                                className={`hidden whitespace-nowrap text-[9px] font-medium uppercase tracking-[0.32em] sm:block ${
                                    dark || lightMenu ? "text-ivory/60" : "text-ink/50"
                                }`}
                            >
                                International Recruitment • Global Mobility
                            </span>
                        </span>
                    </a>

                    <nav
                        aria-label="Primary"
                        className={`hidden items-center gap-5 text-[11px] font-medium uppercase tracking-[0.14em] 2xl:flex ${
                            dark ? "text-ivory/85" : "text-ink/75"
                        }`}
                    >
                        {NAV.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                className="whitespace-nowrap transition-colors hover:text-champagne"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden shrink-0 items-center gap-2.5 2xl:flex">
                        <a
                            href="/account/login"
                            data-testid="header-signin-link"
                            className={`whitespace-nowrap px-1 text-[11px] font-medium uppercase tracking-[0.12em] transition-colors ${
                                dark ? "text-ivory/70 hover:text-champagne" : "text-ink/60 hover:text-forest"
                            }`}
                        >
                            Sign In
                        </a>
                        <a
                            href="/jobs"
                            data-testid="header-find-jobs-btn"
                            className={`whitespace-nowrap border px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] transition-colors ${
                                dark
                                    ? "border-ivory/40 text-ivory hover:border-ivory hover:bg-ivory/10"
                                    : "border-ink/25 text-ink hover:border-forest hover:text-forest"
                            }`}
                        >
                            Find Jobs
                        </a>
                        <a
                            href="/recruitment"
                            data-testid="header-hire-talent-btn"
                            className="whitespace-nowrap bg-forest px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-forest-deep"
                        >
                            Hire Talent
                        </a>
                    </div>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className={`shrink-0 p-2 2xl:hidden ${dark || lightMenu ? "text-ivory" : "text-ink"}`}
                    >
                        {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
                    </button>
                </div>
            </header>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-night px-5 pb-8 pt-28 text-ivory sm:px-6 2xl:hidden"
                >
                    <nav aria-label="Mobile" className="flex flex-col">
                        {NAV.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                onClick={() => setOpen(false)}
                                className="whitespace-nowrap border-b border-ivory/10 py-3.5 font-serif text-2xl font-medium tracking-tight transition-colors hover:text-champagne min-[390px]:text-3xl"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                    <div className="mt-10 flex flex-col gap-3 pt-2">
                        <a
                            href="/recruitment"
                            data-testid="mobile-hire-talent-btn"
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-center whitespace-nowrap bg-forest px-6 py-4 text-sm font-medium uppercase tracking-[0.12em] text-ivory"
                        >
                            Hire Talent
                        </a>
                        <a
                            href="/jobs"
                            data-testid="mobile-find-jobs-btn"
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-center whitespace-nowrap border border-ivory/30 px-6 py-4 text-sm font-medium uppercase tracking-[0.12em] text-ivory"
                        >
                            Find Jobs
                        </a>
                        <a
                            href="/account/login"
                            data-testid="mobile-signin-link"
                            onClick={() => setOpen(false)}
                            className="mt-2 whitespace-nowrap text-center text-xs font-medium uppercase tracking-[0.2em] text-ivory/55 transition-colors hover:text-champagne"
                        >
                            Candidate Sign In
                        </a>
                    </div>
                </div>
            )}
        </>
    );
};
